import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Crown, Sparkles as SparklesIcon, Heart } from "lucide-react";
import Sparkles from "../components/Sparkles";
import LangToggle from "../components/LangToggle";
import { useLang } from "../lib/i18n";
import { getEventConfig } from "../lib/api";
import { formatEventDate, formatEventTime } from "../lib/director";

const PALACE_BG = "/palace-gate.png";

// Manual bypass for testing — accept it ONLY when the URL explicitly contains
// `?open=1`. We deliberately do NOT persist this in localStorage anymore so a
// plain refresh of "/" always shows the doors (with the latest admin schedule)
// instead of silently redirecting to /welcome.
export function bypassActive() {
  if (typeof window === "undefined") return false;
  const qs = new URLSearchParams(window.location.search);
  return qs.get("open") === "1";
}

const TE = { fontFamily: "'Tiro Telugu','Noto Serif Telugu','Yatra One','Eczar',serif" };

// ------------------- Ancient stone countdown tile (responsive) -------------------
const StoneTile = ({ value, label }) => (
  <div className="flex flex-col items-center">
    <div
      className="relative grid place-items-center"
      style={{
        width: "clamp(56px, 17vw, 100px)",
        height: "clamp(68px, 20vw, 120px)",
        background:
          "linear-gradient(180deg, #c89d6a 0%, #b9874f 35%, #8e6131 65%, #6e4a23 100%)",
        borderRadius: "12px 12px 20px 20px",
        boxShadow:
          "inset 0 4px 0 rgba(255,235,200,0.45), inset 0 -8px 0 rgba(0,0,0,0.35), 0 14px 30px rgba(0,0,0,0.55)",
        border: "2px solid #5a3a18",
      }}
    >
      {["tl", "tr", "bl", "br"].map((c) => (
        <span
          key={c}
          className="absolute"
          style={{
            width: 12, height: 12,
            top: c.startsWith("t") ? 4 : "auto",
            bottom: c.startsWith("b") ? 4 : "auto",
            left: c.endsWith("l") ? 4 : "auto",
            right: c.endsWith("r") ? 4 : "auto",
            borderTop: c.startsWith("t") ? "2px solid #FCF6BA" : "none",
            borderBottom: c.startsWith("b") ? "2px solid #FCF6BA" : "none",
            borderLeft: c.endsWith("l") ? "2px solid #FCF6BA" : "none",
            borderRight: c.endsWith("r") ? "2px solid #FCF6BA" : "none",
            borderRadius: 3,
            opacity: 0.85,
          }}
        />
      ))}
      <span
        style={{
          fontFamily: "'Cormorant Garamond',serif",
          fontSize: "clamp(30px, 8vw, 56px)",
          fontWeight: 700,
          color: "#3a1d0a",
          textShadow: "0 1px 0 #f7dba6, 0 -1px 0 rgba(0,0,0,0.4)",
          lineHeight: 1,
          letterSpacing: "0.05em",
        }}
      >
        {String(value).padStart(2, "0")}
      </span>
    </div>
    <p
      className="mt-1.5 tracking-[0.25em] uppercase"
      style={{
        ...TE,
        color: "#FCF6BA",
        textShadow: "0 1px 2px rgba(0,0,0,0.7)",
        fontSize: "clamp(9px, 2.2vw, 12px)",
      }}
    >
      {label}
    </p>
  </div>
);

function Countdown({ targetUtc }) {
  const lang = useLang();
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const tm = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(tm);
  }, []);
  const diff = Math.max(0, targetUtc - now);
  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff % 86400000) / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);
  const labels = lang === "te"
    ? { d: "రోజులు", h: "గంటలు", m: "నిమిషాలు", s: "క్షణాలు" }
    : { d: "Days", h: "Hours", m: "Minutes", s: "Seconds" };
  return (
    <div className="flex gap-2 sm:gap-4 justify-center" data-testid="doors-countdown">
      <StoneTile value={d} label={labels.d} />
      <StoneTile value={h} label={labels.h} />
      <StoneTile value={m} label={labels.m} />
      <StoneTile value={s} label={labels.s} />
    </div>
  );
}

// ------------------- Main Doors Component -------------------
export default function Doors() {
  const lang = useLang();
  const navigate = useNavigate();
  const [opening, setOpening] = useState(false);
  const [now, setNow] = useState(Date.now());
  const [startIso, setStartIso] = useState(null);

  useEffect(() => {
    const tm = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(tm);
  }, []);

  // Pull admin-saved start time so countdown + decree update live.
  // Clear any legacy `anagha_doors_bypass` flag so refresh always honours the
  // current schedule (this was previously persisted and caused a redirect-loop).
  useEffect(() => {
    if (typeof window !== "undefined") {
      try { localStorage.removeItem("anagha_doors_bypass"); } catch { /* ignore */ }
    }
    let alive = true;
    const load = () => getEventConfig().then((c) => { if (alive) setStartIso(c.start); }).catch(() => {});
    load();
    const tm = setInterval(load, 15000);
    return () => { alive = false; clearInterval(tm); };
  }, []);

  const partyStartMs = startIso ? new Date(startIso).getTime() : null;
  const datePassed = partyStartMs !== null && now >= partyStartMs;
  const bypass = bypassActive();

  // When the date passes (or bypass set), play opening celebration → navigate /welcome
  useEffect(() => {
    if (!datePassed && !bypass) return;
    setOpening(true);
    const tm = setTimeout(() => navigate("/welcome", { replace: true }), bypass ? 200 : 3000);
    return () => clearTimeout(tm);
  }, [datePassed, bypass, navigate]);

  return (
    <div
      className="fixed inset-0 overflow-hidden text-blush"
      data-testid="kingdom-doors"
      style={{ background: "#2a1a14" }}
    >
      {/* Palace gate as the actual background image */}
      <img
        src={PALACE_BG}
        alt=""
        aria-hidden
        className="absolute inset-0 w-full h-full object-cover"
        style={{
          // Slight zoom + Ken Burns drift for life
          animation: "kenBurnsPalace 24s ease-in-out infinite alternate",
          // Slightly blur behind the content so text reads well; clears during opening
          filter: opening ? "blur(0px) brightness(1.1)" : "blur(2px) brightness(0.92)",
          transition: "filter 1s ease",
        }}
      />
      {/* Warm vignette + readability gradient */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse at 50% 30%, transparent 0%, rgba(20,8,4,0.35) 55%, rgba(20,8,4,0.78) 100%)",
        }}
      />
      {/* Soft gold haze at the top for sunset feel */}
      <div
        className="absolute inset-x-0 top-0 h-1/3 pointer-events-none"
        style={{ background: "linear-gradient(180deg, rgba(252,235,170,0.18), transparent)" }}
      />
      <Sparkles count={60} />

      {/* Language toggle — fixed above everything */}
      <div className="fixed top-3 right-3 sm:top-5 sm:right-5 z-[100]">
        <LangToggle light />
      </div>

      {/* Opening celebration overlay */}
      {opening && (
        <>
          <div className="absolute inset-0 pointer-events-none z-[60]">
            {Array.from({ length: 60 }).map((_, i) => {
              const angle = (i / 60) * Math.PI * 2;
              const dist = 280 + Math.random() * 380;
              const dx = Math.cos(angle) * dist;
              const dy = Math.sin(angle) * dist;
              const colors = ["#FCF6BA", "#D4AF37", "#FF7AA8", "#FFC4D6", "#FFE5A0", "#B97AC6"];
              return (
                <span
                  key={i}
                  className="absolute left-1/2 top-1/2 rounded-full"
                  style={{
                    width: 10, height: 10,
                    background: colors[i % colors.length],
                    boxShadow: `0 0 12px ${colors[i % colors.length]}`,
                    ["--cx"]: `${dx}px`,
                    ["--cy"]: `${dy}px`,
                    animation: `confettiBurst ${1.4 + Math.random() * 0.9}s ease-out ${0.15 + Math.random() * 0.4}s forwards`,
                  }}
                />
              );
            })}
          </div>
          <div className="absolute inset-0 z-40 grid place-items-center pointer-events-none px-4">
            <div className="text-center animate-[fadeIn_0.8s_ease-out]">
              <Crown className="mx-auto text-[#FCF6BA] drop-shadow-2xl" size={64} />
              <p
                className="font-script gold-text mt-3 drop-shadow-2xl"
                style={{ ...TE, fontSize: "clamp(34px, 8vw, 78px)" }}
              >
                {lang === "te" ? "రాజ్యం తెరుచుకుంది!" : "The Kingdom Opens!"}
              </p>
            </div>
          </div>
        </>
      )}

      {/* Foreground content (hidden during opening) */}
      {!opening && (
        <div
          className="relative z-30 w-full h-full flex flex-col items-center px-4 sm:px-6 text-center"
          style={{
            // Push content down a bit so palace dome shows on top, but keep it scrollable on tiny screens
            paddingTop: "clamp(48px, 9vh, 110px)",
            paddingBottom: "clamp(20px, 4vh, 40px)",
            overflowY: "auto",
          }}
          data-testid="doors-content"
        >
          {/* Royal Gates · Sealed pill */}
          <div className="inline-flex items-center gap-2 glass rounded-full px-4 py-1.5 text-plum mb-3">
            <Crown size={14} className="text-[#B38728]" />
            <span
              className="label-eyebrow"
              style={{ ...TE, fontSize: "clamp(10px, 2.6vw, 12px)" }}
            >
              {lang === "te" ? "రాజ్య ద్వారాలు · మూసివేయబడ్డాయి" : "Royal Gates · Sealed"}
            </span>
          </div>

          {/* Title */}
          <h1
            className="font-display font-semibold leading-[0.95] mb-2"
            style={{
              ...TE,
              fontSize: "clamp(38px, 9vw, 96px)",
              filter: "drop-shadow(0 4px 22px rgba(0,0,0,0.75))",
            }}
          >
            <span className="gold-text">
              {lang === "te" ? "అనఘ రాజ్యం" : "Anagha's Kingdom"}
            </span>
          </h1>
          <p
            className="font-script text-blush drop-shadow-lg mb-4 sm:mb-5"
            style={{ ...TE, fontSize: "clamp(18px, 4.6vw, 36px)" }}
          >
            {lang === "te"
              ? "ద్వారాలు త్వరలో తెరుచుకుంటాయి…"
              : "The Royal Doors open soon…"}
          </p>

          {/* Royal decree on parchment card */}
          <div
            className="w-full max-w-2xl mx-auto mb-5"
            style={{
              background: "linear-gradient(180deg, #f7e7c6 0%, #e8cf99 100%)",
              border: "2px solid #8B6A1A",
              boxShadow: "0 16px 50px rgba(0,0,0,0.55), inset 0 0 30px rgba(139,106,26,0.25)",
              borderRadius: "16px 16px 32px 32px / 16px 16px 60px 60px",
              padding: "clamp(14px, 4vw, 26px) clamp(16px, 5vw, 32px)",
            }}
          >
            <p
              className="font-display text-[#3b1f08] leading-relaxed italic"
              style={{ ...TE, fontSize: "clamp(13px, 3.5vw, 19px)" }}
            >
              {lang === "te" ? (
                <>
                  &ldquo;యువరాణి అనఘ ఆజ్ఞాపించే వరకు రాజ్య ద్వారాలు మూసివేయబడి ఉంటాయి.
                  {startIso && <> {formatEventDate(startIso, "te")} {formatEventTime(startIso, "te")}కు</>} ఆమె చిన్ని పాదంతో ద్వారాలను తెరిచి
                  వేడుకను ప్రారంభిస్తుంది.&rdquo;
                </>
              ) : (
                <>
                  &ldquo;The Royal Doors stand sealed until Princess Anagha commands.
                  {startIso && <> On {formatEventDate(startIso, "en")} at {formatEventTime(startIso, "en")},</>} she will tap the gates with her
                  tiny foot and begin the celebration.&rdquo;
                </>
              )}
            </p>
            <p
              className="font-script text-[#5a3a18] text-right mt-2"
              style={{ ...TE, fontSize: "clamp(15px, 3.8vw, 22px)" }}
            >
              — {lang === "te" ? "మహారాజ ఆజ్ఞ" : "Royal Decree"} ✦
            </p>
          </div>

          {/* Stone-tile countdown — only rendered once we know the real start time */}
          {partyStartMs && <Countdown targetUtc={partyStartMs} />}
          {!partyStartMs && (
            <p className="label-eyebrow text-blush/70 mt-2" style={TE}>
              {lang === "te" ? "రాజ సందేశం లోడ్ అవుతోంది..." : "Loading royal schedule..."}
            </p>
          )}

          {/* Date/time eyebrow — also gated on a real schedule */}
          {startIso && (
            <div className="mt-4 sm:mt-5 flex items-center gap-2 text-blush/95 drop-shadow">
              <Heart size={14} className="text-[#FFC4D6]" fill="#FFC4D6" />
              <p className="label-eyebrow" style={{ ...TE, fontSize: "clamp(10px, 2.6vw, 13px)" }}>
                {formatEventDate(startIso, lang)} · {formatEventTime(startIso, lang)}
              </p>
              <SparklesIcon size={14} className="text-[#FCF6BA]" />
            </div>
          )}
        </div>
      )}

      <style>{`
        @keyframes kenBurnsPalace {
          0%   { transform: scale(1.02) translate(0, 0); }
          100% { transform: scale(1.08) translate(-1%, -2%); }
        }
        @keyframes confettiBurst {
          0%   { transform: translate(-50%, -50%) scale(0.4); opacity: 1; }
          70%  { opacity: 1; }
          100% { transform: translate(calc(-50% + var(--cx)), calc(-50% + var(--cy))) scale(0.2); opacity: 0; }
        }
        @keyframes fadeIn { from { opacity: 0; transform: scale(0.9) } to { opacity: 1; transform: scale(1) } }
      `}</style>
    </div>
  );
}
